/** Link an existing case to the asset, unlink one, and make a shared case internal again. */

import { useState } from 'react';

import { useCases, useLinkCase, useMakeCaseInternal, useUnlinkCase } from '@/api/queries/cases';
import type { CaseOut } from '@/api/types/cases';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

import type { MaterialWriter } from '../materials/MaterialDialogs';

export function LinkCaseDialog({ writer, linked, onClose }: { writer: MaterialWriter; linked: string[]; onClose: () => void }) {
  const all = useCases(null);
  const link = useLinkCase(writer.assetId);
  const [chosen, setChosen] = useState('');
  const offered = (all.data?.cases ?? []).filter((c) => !linked.includes(c.id));

  return (
    <Modal
      title="Link existing case"
      size="narrow"
      onClose={onClose}
      onSubmit={() => chosen && link.mutate({ caseId: chosen, revision: writer.revision }, { onSuccess: (a) => (writer.saw(a), onClose()) })}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <ActionButton state={buttonState(link)} label="Link case" disabled={!chosen} />
        </>
      }
    >
      <div className="stack-md">
        <FormError error={link.error ?? all.error} />
        {all.isPending && <p className="hint" role="status">Loading cases…</p>}
        {all.isSuccess && offered.length === 0 && <p>Every case is already linked to this asset.</p>}
        {offered.length > 0 && (
          <div className="field">
            <label htmlFor="link-case">Case</label>
            <select id="link-case" autoFocus value={chosen} onChange={(e) => setChosen(e.target.value)}>
              <option value="" disabled>Choose a case</option>
              {offered.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                  {c.assets.length > 0 ? ` (${c.assets.map((a) => a.name).join(', ')})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </Modal>
  );
}

export function UnlinkCaseDialog({ writer, item, onClose }: { writer: MaterialWriter; item: CaseOut; onClose: () => void }) {
  const unlink = useUnlinkCase(writer.assetId);
  return (
    <ConfirmDialog
      title={`Unlink ${item.label}?`}
      confirmLabel="Unlink"
      danger
      pending={unlink.isPending}
      error={unlink.error}
      onClose={onClose}
      onConfirm={() => unlink.mutate({ caseId: item.id, revision: writer.revision }, { onSuccess: (a) => (writer.saw(a), onClose()) })}
    >
      <p>The case stays in the catalog and on any other asset it is linked to; it no longer counts as proof for this one.</p>
    </ConfirmDialog>
  );
}

export function MakeInternalDialog({ item, onClose }: { item: CaseOut; onClose: () => void }) {
  const internal = useMakeCaseInternal();
  return (
    <ConfirmDialog
      title={`Make ${item.label} internal?`}
      confirmLabel="Make internal"
      pending={internal.isPending}
      error={internal.error}
      onClose={onClose}
      onConfirm={() => internal.mutate(item.id, { onSuccess: onClose })}
    >
      <p>Clients, the client PDF and the Advisor&apos;s client brief stop showing it. Sharing it again needs a new review of the anonymized version.</p>
    </ConfirmDialog>
  );
}
